// ============================================================================
// store.ts — ЕДИНЫЙ источник языка интерфейса клиента (этап 2).
//
// Модель (SSoT):
//   1) выбор пользователя — user_settings `ui.locale` (своя настройка) +
//      зеркало localStorage (нужно до входа/на лаунчере);
//   2) язык базы по умолчанию — ConfigInfo.language (ветка «Языки»);
//   3) доступные языки — metadata.ListLanguages + встроенные (ru/en).
//
// Словари:
//   • встроенные (messages.ts) — работают без сети/базы;
//   • серверные (metadata.GetTranslations(locale)) — НАКЛАДЫВАЮТСЯ поверх
//     встроенных, правка текста в конфигураторе не требует пересборки клиента.
//
// Fallback ЯВНЫЙ и детерминированный:
//   текущая локаль (сервер → встроенный словарь)
//   → язык базы по умолчанию (сервер → встроенный)
//   → сам ключ (видно, что перевода нет; никаких «тихих» подстановок).
//
// Новых RPC не требуется: GetTranslations и ListLanguages уже есть в proto.
// ============================================================================

import { getSDK } from '@/store/sdkStore';
import { useAuthStore } from '@/store/authStore';
import { useCenterStore } from '@/store/centerStore';
import { settingsStore } from '@/lib/settingsStore';
import { createLogger } from '@/lib/logger';
import { BASE_LOCALE, BUILTIN_LANGUAGES, MESSAGES, type Messages } from './messages';

const log = createLogger('i18n');

/** Ключ пользовательской настройки (перекрывает язык базы). */
export const LOCALE_SETTING_KEY = 'ui.locale';
/** Зеркало для лаунчера/формы входа (серверных настроек там ещё нет). */
const LOCALE_STORAGE_KEY = 'hydra_locale';

export interface I18nLanguage {
  code: string;
  label: string;
}

export interface I18nSnapshot {
  /** Фактически применяемая локаль. */
  locale: string;
  /** Язык базы по умолчанию (явный fallback). */
  defaultLocale: string;
  /** Доступные языки интерфейса. */
  languages: I18nLanguage[];
  /** Словари встроенные/серверные загружены (встроенные — всегда). */
  ready: boolean;
  /** Растёт при любом изменении — подписка в React. */
  revision: number;
}

type Listener = () => void;

const listeners = new Set<Listener>();
let dicts: Record<string, Messages> = {};
let inFlight: Promise<void> | null = null;

function readPersistedLocale(): string {
  try {
    return String(localStorage.getItem(LOCALE_STORAGE_KEY) || '');
  } catch {
    return '';
  }
}

function persistLocale(code: string): void {
  try {
    if (code) localStorage.setItem(LOCALE_STORAGE_KEY, code);
    else localStorage.removeItem(LOCALE_STORAGE_KEY);
  } catch {
    // localStorage недоступен (приватный режим) — язык живёт в памяти,
    // это не ошибка: явный fallback продолжает работать.
  }
}

function mergeLanguages(server: Array<{ name?: string; synonym?: string }>): I18nLanguage[] {
  const out: I18nLanguage[] = [];
  const seen = new Set<string>();
  for (const l of BUILTIN_LANGUAGES) {
    out.push(l);
    seen.add(l.code);
  }
  for (const l of server) {
    const code = String(l?.name || '').trim();
    if (!code || seen.has(code)) continue;
    seen.add(code);
    out.push({ code, label: String(l?.synonym || '').trim() || code });
  }
  return out;
}

function resolveLocale(requested: string, defaultLocale: string, languages: I18nLanguage[]): string {
  const req = String(requested || '').trim();
  if (req && (languages.some((l) => l.code === req) || MESSAGES[req])) return req;
  const def = String(defaultLocale || '').trim();
  if (def && (languages.some((l) => l.code === def) || MESSAGES[def])) return def;
  return BASE_LOCALE;
}

let snapshot: I18nSnapshot = {
  locale: resolveLocale(readPersistedLocale(), BASE_LOCALE, BUILTIN_LANGUAGES),
  defaultLocale: BASE_LOCALE,
  languages: BUILTIN_LANGUAGES,
  ready: false,
  revision: 0,
};

function emit(next: Partial<I18nSnapshot>): void {
  snapshot = { ...snapshot, ...next, revision: snapshot.revision + 1 };
  for (const l of listeners) l();
}

function applyDocumentLang(locale: string): void {
  if (typeof document !== 'undefined') document.documentElement.lang = locale;
}

/** Встроенный словарь локали (или пустой — язык без встроенных строк допустим). */
function builtinDict(locale: string): Messages {
  return MESSAGES[locale] || {};
}

/** Загрузка серверного словаря одной локали (идемпотентно). */
async function loadDictionary(locale: string, force = false): Promise<void> {
  if (!locale) return;
  if (!force && dicts[locale]) return;
  try {
    const r: any = await getSDK().metadata.getTranslations({ locale });
    const d: Messages = {};
    for (const item of (r?.items || []) as any[]) {
      const key = String(item?.key || '');
      if (!key) continue;
      const value = item?.value == null ? '' : String(item.value);
      if (value !== '') d[key] = value;
    }
    dicts = { ...dicts, [locale]: d };
  } catch (e: any) {
    // Словарь — ДОПОЛНЕНИЕ: без сервера интерфейс работает на встроенных
    // строках, поэтому ошибка не блокирует приложение, но фиксируется явно.
    log.warn('dictionary load failed', { locale, error: e?.message || String(e) });
    dicts = { ...dicts, [locale]: dicts[locale] || {} };
  }
}

/**
 * Загрузка ресурсов языка для активной базы. Вызывается провайдером при
 * смене базы/входе и принудительно — после сохранения переводов.
 */
export async function loadI18nResources(force = false): Promise<void> {
  const slug = String(useCenterStore.getState().baseSlug || '');
  if (!slug) {
    // Лаунчер/форма входа: только встроенные словари (явный BASE_LOCALE).
    applyDocumentLang(snapshot.locale);
    emit({
      defaultLocale: BASE_LOCALE,
      languages: BUILTIN_LANGUAGES,
      locale: resolveLocale(readPersistedLocale(), BASE_LOCALE, BUILTIN_LANGUAGES),
      ready: true,
    });
    return;
  }
  if (inFlight && !force) return inFlight;

  inFlight = (async () => {
    let defaultLocale = BASE_LOCALE;
    let serverLanguages: Array<{ name?: string; synonym?: string }> = [];

    try {
      const cfg: any = await getSDK().config.getConfigInfo({});
      const lang = String(cfg?.language || '').trim();
      if (lang) defaultLocale = lang;
    } catch (e: any) {
      log.warn('base default language unavailable — fallback to ru', { error: e?.message || String(e) });
    }

    try {
      const r: any = await getSDK().metadata.listLanguages({});
      serverLanguages = Array.isArray(r?.items) ? r.items : [];
    } catch (e: any) {
      log.warn('languages list unavailable — built-in only', { error: e?.message || String(e) });
    }

    const languages = mergeLanguages(serverLanguages);
    // Локаль вычисляем ДО загрузки и ПЕРЕсчитываем ПОСЛЕ: пользователь мог
    // переключить язык, пока шли запросы (иначе его выбор был бы затёрт).
    const initialLocale = resolveLocale(readPersistedLocale(), defaultLocale, languages);
    await Promise.all([
      loadDictionary(defaultLocale, force),
      initialLocale !== defaultLocale ? loadDictionary(initialLocale, force) : Promise.resolve(),
    ]);
    const locale = resolveLocale(readPersistedLocale(), defaultLocale, languages);
    if (locale !== initialLocale) await loadDictionary(locale, force);

    applyDocumentLang(locale);
    emit({ defaultLocale, languages, locale, ready: true });
  })().finally(() => {
    inFlight = null;
  });

  return inFlight;
}

/** Применить сохранённую пользовательскую настройку языка (после гидрации). */
export function syncLocaleFromSettings(): void {
  const fromSettings = String(settingsStore.get(LOCALE_SETTING_KEY) || '').trim();
  if (!fromSettings) return;
  if (fromSettings === readPersistedLocale()) return;
  persistLocale(fromSettings);
  const locale = resolveLocale(fromSettings, snapshot.defaultLocale, snapshot.languages);
  if (locale === snapshot.locale) {
    // Локаль та же (например, язык совпал с базовым), но выбор пользователя
    // нужно сохранить в снапшоте — иначе следующий emit его потеряет.
    emit({ locale });
    return;
  }
  void loadDictionary(locale).then(() => {
    applyDocumentLang(locale);
    emit({ locale });
  });
}

/**
 * Сменить язык интерфейса. Пользовательская настройка пишется в user_settings
 * (своя) только для авторизованного пользователя; до входа — localStorage.
 */
export function setLocale(code: string): void {
  const next = resolveLocale(code, snapshot.defaultLocale, snapshot.languages);
  persistLocale(next);
  if (useAuthStore.getState().isAuthenticated) {
    settingsStore.set(LOCALE_SETTING_KEY, next);
  }
  if (next === snapshot.locale) return;
  void loadDictionary(next).then(() => {
    applyDocumentLang(next);
    emit({ locale: next });
  });
}

/** Интерполяция {name}-плейсхолдеров; отсутствующее значение — пусто. */
export function interpolate(template: string, vars?: Record<string, string | number>): string {
  if (!vars) return template;
  return template.replace(/\{(\w+)\}/g, (m, name: string) => {
    const v = vars[name];
    return v === undefined || v === null ? '' : String(v);
  });
}

/**
 * Перевод ключа. Явный fallback: текущая локаль → язык по умолчанию → ключ.
 * Возврат ключа — намеренный маркер отсутствующего перевода (не «пусто»).
 */
export function translate(key: string, vars?: Record<string, string | number>): string {
  const cur = snapshot.locale;
  const def = snapshot.defaultLocale;
  const value = dicts[cur]?.[key]
    ?? builtinDict(cur)[key]
    ?? dicts[def]?.[key]
    ?? builtinDict(def)[key];
  if (value === undefined) return key;
  return interpolate(value, vars);
}

export function getI18nSnapshot(): I18nSnapshot {
  return snapshot;
}

export function subscribeI18n(fn: Listener): () => void {
  listeners.add(fn);
  return () => { listeners.delete(fn); };
}
